import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import { Droppable, Draggable } from "react-beautiful-dnd";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from '@mui/material/TableSortLabel';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Paper } from '@mui/material';
import PopMenu from "./PopMenu";
import { Button } from '@mui/material';
import { IconButton, } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from "@mui/material/Checkbox";

const EventDragTable = ({ row, heading, noEvent, droppableId, handleEditClick, handleCheckboxChange, handleSelectAll , openPopup, selectedIds }) => {
  const minWidth = 700;
  
  // Sorting state
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [sortedRows, setSortedRows] = useState(row);

  // Map heading to field names for sorting
  // Map heading to field names for sorting
const fieldMappings = {
  'Event': 'primaryEventName', 
  'Callout Name': 'callOutName',
  // 'Publish': 'publish'
};

// Function to handle sort request
const handleRequestSort = (property) => {
  const isAsc = orderBy === property && order === 'asc';
  const newOrder = isAsc ? 'desc' : 'asc';
  setOrder(newOrder);
  setOrderBy(property);
  
  // Sort the rows
  const sortedData = [...row].sort((a, b) => {
    // Handle null or undefined values
    if (!a[property]) return newOrder === 'asc' ? -1 : 1;
    if (!b[property]) return newOrder === 'asc' ? 1 : -1;
    
    // Handle string comparison (case insensitive)
    if (typeof a[property] === 'string' && typeof b[property] === 'string') {
      return newOrder === 'asc' 
        ? a[property].toLowerCase().localeCompare(b[property].toLowerCase()) 
        : b[property].toLowerCase().localeCompare(a[property].toLowerCase());
    } 
    // Handle number comparison
    else {
      return newOrder === 'asc' ? a[property] - b[property] : b[property] - a[property];
    }
  });
  
  setSortedRows(sortedData);
};

  // Use sortedRows if we have sorting, otherwise use the original rows
  const displayRows = orderBy ? sortedRows : row;
  
  // Update sortedRows when row prop changes
  React.useEffect(() => {
    setSortedRows(row);
    // If sorting was active, maintain it with new data
    if (orderBy) {
      handleRequestSort(orderBy);
    }
  }, [row]);

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      fontSize: 15,
      color: "#757ea5",
      backgroundColor: theme.palette.common.white,
      borderBottom: "1px solid #cfd4e9",
      padding: "8px",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 15,
      fontWeight: "bold",
      padding: "0px 10px",
      fontFamily: "Montserrat, sans-serif.SemiBold",
      margin: "5px 0px",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: "#F3FBFF",
    },
    "&:nth-of-type(even)": {
      backgroundColor: "#FFFFFF",
    },
    "&:last-child td": {
      border: 0,
    },
    height: "45px",
    alignContent: "center",
  }));
      
  const EmptyTableMessage = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '5px',
      padding: '40px 0',
      fontSize: '22px',
      transform: 'translate(0%,70%)'
    }}>
      <DragIndicatorIcon style={{ color: "#757ea5" }} />
      <p style={{color: "#757ea5", fontSize: 19}}>Drag & Drop from Primary Events.</p>
    </div>
  );


  // const menu = ['Edit', 'Delete'];
  const menu = ['Delete'];

  return (
    <TableContainer
      component={Paper}
      sx={{
        border: "1px solid #cfd4e9",
        minHeight: '45%',
        maxHeight: '60%',
        width: "96%",
        margin: "20px 30px",
        overflow: 'auto',

        /* Custom Scrollbar Styles */
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "#cfd4e9",
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "#f0f0f0",
        },
        "&::-webkit-scrollbar-button": {
          display: "none",
        },
      }}
    >
      {noEvent || row.length === 0 ? (
        <Droppable droppableId={droppableId} type="event">
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={{
                backgroundColor: snapshot.isDraggingOver ? '#e3f2fd' : 'inherit',
                transition: 'background-color 0.2s ease',
                height: '100%',
              }}
            >
              <EmptyTableMessage />
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      ) : (
        <Table sx={{ minWidth: minWidth }} aria-label="customized table" stickyHeader>
          <TableHead>
            <TableRow style={{ borderBottom: "1px solid #cfd4e9" }}>
                {/* Header Checkbox */}
              <StyledTableCell padding="checkbox">
                  <Checkbox
                indeterminate={
                  selectedIds.length > 0 &&
                  selectedIds.length < displayRows.length
                }
                checked={
                  displayRows.length > 0 && selectedIds.length === displayRows.length
                }
                onChange={handleSelectAll}
              /> 
              </StyledTableCell>
              {heading.map((head, index) => {
                // First column (drag) and last column (menu) should not be sortable
                const isSortable = index > 0 && index < heading.length - 1;
                const fieldName = fieldMappings[head];
                
                return (
                  <StyledTableCell key={index}>
                    {isSortable && fieldName ? (
                      <TableSortLabel
                        active={orderBy === fieldName}
                        direction={orderBy === fieldName ? order : 'asc'}
                        onClick={() => handleRequestSort(fieldName)}
                        sx={{ 
                          color: "#757ea5",
                          '&.MuiTableSortLabel-active': {
                            color: "#02ACF6",
                          },
                        }}
                      >
                        {head}
                      </TableSortLabel>
                    ) : (
                      head
                    )}
                  </StyledTableCell>
                );
              })}
            </TableRow>
          </TableHead>

          <Droppable droppableId={droppableId} type="event">
            {(provided, snapshot) => (
              <TableBody 
                {...provided.droppableProps} 
                ref={provided.innerRef}
                style={{
                  backgroundColor: snapshot.isDraggingOver ? '#e3f2fd' : 'inherit',
                  transition: 'background-color 0.2s ease'
                }}
              >
                {displayRows.map((item, index) => (
                  <Draggable
                    key={item.sNo ? item.sNo.toString() : "0"}
                    draggableId={item.sNo ? item.sNo.toString() : "0"}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <StyledTableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          opacity: snapshot.isDragging ? 0.8 : 1
                        }}
                      >
                       
                        {/* Checkbox column */}
                        <StyledTableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(item.eventId)}
                            onChange={() => handleCheckboxChange(item)}
                          />
                        </StyledTableCell>
 {<StyledTableCell 
                          align="left" 
                          {...provided.dragHandleProps}
                        >
                          <DragIndicatorIcon style={{ color: "#757ea5" }} />
                        </StyledTableCell> }
                        <StyledTableCell sx={{ width: `calc(${minWidth} * 30 / 100)` }}>
                          <p style={{color:'#02acf6',cursor:'pointer'}}>
                            {item.callOutName}
                          </p>
                        </StyledTableCell>
                        

                      </StyledTableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </Table>
      )}
    </TableContainer>
  );
};

export default EventDragTable;